import { motion } from "framer-motion";
import { Building2, MapPin, Route } from "lucide-react";
import { SectionTransition } from "@/components/SectionTransition";

const EASE = [0.16, 1, 0.3, 1];

const REGIONS = [
  "Ventura County",
  "Greater Los Angeles",
  "Inland Empire",
  "Orange County",
  "San Diego County",
  "High Desert + surrounding markets",
];

const CORRIDORS = ["US-101", "I-5", "I-10", "I-15", "I-405", "SR-60", "I-110"];

const LOCAL_KNOWLEDGE = [
  "Traffic-sensitive pickup windows",
  "Warehouse and receiver schedules",
  "Regional equipment requirements",
  "Multi-stop and time-sensitive coordination",
];

export default function SouthernCalifornia() {
  return (
    <section
      id="southern-california"
      data-testid="southern-california-section"
      className="relative overflow-hidden border-y border-[#222736] bg-[#0C0E13] py-20 md:py-24"
    >
      <SectionTransition testId="capabilities-socal-transition-line" />
      <div className="grid-lines absolute inset-0 opacity-20" />
      <div className="relative mx-auto grid w-full max-w-7xl gap-12 px-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-center lg:px-10">
        <motion.div
          initial={{ opacity: 0, x: -32 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-70px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <div className="flex items-center gap-3">
            <MapPin className="h-4 w-4 text-[#FF5500]" />
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-[#F59E0B]">
              SOUTHERN CALIFORNIA // LOCAL EXPERTISE
            </p>
          </div>
          <h2 className="mt-5 font-display text-3xl font-black uppercase text-white sm:text-4xl lg:text-5xl">
            Southern California Freight, Understood.
          </h2>
          <p className="mt-5 max-w-xl text-base leading-7 text-slate-300 lg:text-[17px] lg:leading-8">
            Based in Oxnard, Merchant Alliance Brokerage is very active throughout all of Southern California. We understand the regional corridors, traffic patterns, business centers, and scheduling details that shape freight movement across the area.
          </p>
          <div className="mt-8 flex items-center gap-4 border-l-2 border-[#FF5500] pl-5">
            <Building2 className="h-5 w-5 shrink-0 text-[#F59E0B]" />
            <p className="font-mono text-xs font-semibold leading-relaxed text-slate-300">
              OXNARD BASED · SOUTHERN CALIFORNIA ACTIVE · 50-STATE COORDINATION
            </p>
          </div>
        </motion.div>

        <motion.div
          data-testid="southern-california-coverage-panel"
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-70px" }}
          transition={{ duration: 0.85, delay: 0.08, ease: EASE }}
          className="border border-[#383F56] bg-[#12141C]/90"
        >
          <div className="flex items-center justify-between gap-5 border-b border-[#222736] px-6 py-5 sm:px-8">
            <div className="flex items-center gap-3">
              <Route className="h-4 w-4 text-[#FF5500]" />
              <span className="font-mono text-[10px] font-bold tracking-[0.2em] text-slate-300">
                REGIONAL OPERATING VIEW
              </span>
            </div>
            <span className="font-mono text-[10px] tracking-[0.18em] text-[#10B981]">
              SOCAL
            </span>
          </div>

          <div className="grid sm:grid-cols-2">
            {REGIONS.map((region, index) => (
              <div
                key={region}
                data-testid={`southern-california-region-${index + 1}`}
                className="flex min-h-16 items-center gap-3 border-b border-[#222736] px-6 py-4 odd:sm:border-r sm:px-8"
              >
                <span className="font-mono text-[10px] text-[#FF5500]">0{index + 1}</span>
                <span className="text-base font-semibold text-slate-100">{region}</span>
              </div>
            ))}
          </div>

          <div className="grid gap-7 px-6 py-6 sm:grid-cols-2 sm:px-8">
            <div>
              <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">
                FAMILIAR CORRIDORS
              </p>
              <p data-testid="southern-california-corridors" className="mt-3 text-base leading-7 text-slate-200">
                {CORRIDORS.join(" · ")}
              </p>
            </div>
            <div>
              <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-slate-500">
                LOCAL OPERATING KNOWLEDGE
              </p>
              <p data-testid="southern-california-local-knowledge" className="mt-3 text-base leading-7 text-slate-200">
                {LOCAL_KNOWLEDGE.join(" · ")}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}